import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Search, User, ChevronDown, Settings, LogOut } from 'lucide-react';
import {useAuth} from '@/components/auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '@shared/Button';
import Input from '@shared/Input';
import T from "@/utils/i18n";

interface HeaderProps {
  isSidebarCollapsed: boolean;
}

const Header: React.FC<HeaderProps> = ({ isSidebarCollapsed }) => { // Accept isSidebarCollapsed prop
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getPageTitle = (): string => {
    const path = location.pathname.indexOf('/',2) > 0 ? location.pathname.slice(0,location.pathname.indexOf('/',2)) : location.pathname;
    switch (path) {
      case '/dashboard':
        return 'Dashboard';
      case '/chat':
        return 'AI Assistant';
      case '/documents':
        return 'Documents';
      case '/analytics':
        return 'Analytics';
      case '/users':
        return 'Users';
      case '/settings':
        return 'Settings';
      default:
        return T.product.name["en"].concat(' Admin');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Dynamically set header width and left position based on sidebar collapse state
  // On mobile, the header should always span full width.
  const headerWidthClass = isSidebarCollapsed ? 'md:w-[calc(100%-100px)]' : 'md:w-[calc(100%-256px)]'; // 256px for expanded, 100px for collapsed
  const headerLeftClass = isSidebarCollapsed ? 'md:left-[100px]' : 'md:left-[256px]'; // Match sidebar width for left positioning

  return (
    <header className={`bg-white shadow-sm px-4 sm:px-6 md:px-8 py-4 flex items-center justify-between fixed top-0 left-0 right-0 ${headerLeftClass} ${headerWidthClass} transition-all duration-300 z-40`}>
      {/* Added pl-16 for mobile to prevent overlap with hamburger menu, md:pl-0 to reset on larger screens */}
      <h1 className="text-2xl font-semibold text-gray-900 pl-16 md:pl-0 flex-shrink-0 mr-4 truncate">{getPageTitle()}</h1>

      <div className="flex items-center space-x-4">
        {/* Search Bar (Visual Only in Phase 1) */}
        <div className="relative hidden md:block">
          <Input
            type="text"
            placeholder="Search..."
            className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
          />
          <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        </div>

        {/* Notifications Icon (Visual Only in Phase 1) */}
        <div className="relative">
          <Button variant="ghost" className="relative p-2 rounded-full hover:bg-gray-100">
            <Bell className="w-5 h-5 text-gray-700" />
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-error-500 border border-white"></span>
          </Button>
        </div>

        {/* User Avatar/Name with Dropdown */}
        <div className="relative">
          <Button variant="ghost" className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-200" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white font-medium">
              {user?.name ? user.name.charAt(0).toUpperCase() : <User className="w-4 h-4" />}
            </div>
            <span className="hidden md:block text-gray-700 font-medium">{user?.name || 'Admin'}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </Button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-40">
              <div className="block px-4 py-2 text-xs text-gray-400">{user?.email || 'admin@example.com'}</div>
              <hr className="my-1 border-gray-100" />
              <Link to={'/users/'.concat(user?.id || '')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsDropdownOpen(false)}>
                <User className="w-4 h-4" /> Profile
              </Link>
              <Link to="/settings" className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsDropdownOpen(false)}>
                <Settings className="w-4 h-4" /> Settings
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
